import { useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ReactFlowProvider } from '@xyflow/react'
import { JsonPane } from './JsonPane'
import { CanvasPane } from './CanvasPane'
import { ResizableDivider } from './ResizableDivider'
import { DocHeader } from './DocHeader'
import { useGraphStore } from '../stores/useGraphStore'
import { useDocumentStore } from '../stores/useDocumentStore'
import { cleanStringify, tryParse } from '../lib/cleanJson'

const AUTO_SAVE_DEBOUNCE_MS = 1500
const DRAFT_KEY_PREFIX = 'graph-app:draft:'

export function DocEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const load = useDocumentStore((s) => s.load)
  const save = useDocumentStore((s) => s.save)
  const markDirty = useDocumentStore((s) => s.markDirty)
  const clear = useDocumentStore((s) => s.clear)
  const doc = useDocumentStore((s) => s.doc)
  const loading = useDocumentStore((s) => s.loading)
  const loadError = useDocumentStore((s) => s.loadError)

  const leftPaneWidth = useGraphStore((s) => s.leftPaneWidth)
  const setLeftPaneWidth = useGraphStore((s) => s.setLeftPaneWidth)
  const jsonText = useGraphStore((s) => s.jsonText)
  const hydrateFromServer = useGraphStore((s) => s.hydrateFromServer)
  const resetGraph = useGraphStore((s) => s.reset)

  const didHydrateRef = useRef(false)
  const saveTimerRef = useRef<number | null>(null)
  // Tracks the last JSON string we know is in sync with the server.
  // Anything different from this = unsaved changes.
  const lastSyncedTextRef = useRef<string | null>(null)

  // Load doc on mount / id change
  useEffect(() => {
    didHydrateRef.current = false
    lastSyncedTextRef.current = null
    if (id) load(id)
    return () => {
      clear()
      resetGraph()
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // When doc arrives, hydrate the graph store. Prefer localStorage draft if it exists and differs.
  useEffect(() => {
    if (!doc || didHydrateRef.current) return
    const draftKey = DRAFT_KEY_PREFIX + doc.id
    let initialValue = doc.content
    let hasDraft = false
    try {
      const draft = window.localStorage.getItem(draftKey)
      if (draft) {
        const parsed = tryParse(draft)
        if (parsed.ok) {
          const serverStr = JSON.stringify(doc.content)
          const draftStr = JSON.stringify(parsed.value)
          if (draftStr !== serverStr) {
            initialValue = parsed.value
            hasDraft = true
          }
        }
      }
    } catch {
      /* ignore */
    }

    const initialText = cleanStringify(initialValue)
    hydrateFromServer(doc.id, initialValue)
    // Server "knows" doc.content; if we hydrated with a draft, the synced text is the server's version.
    lastSyncedTextRef.current = hasDraft ? cleanStringify(doc.content) : initialText
    didHydrateRef.current = true

    if (hasDraft) {
      // Push the draft up immediately
      save(initialValue)
        .then(() => {
          lastSyncedTextRef.current = initialText
          try {
            window.localStorage.removeItem(draftKey)
          } catch {
            /* ignore */
          }
        })
        .catch(() => undefined)
    }
  }, [doc, hydrateFromServer, save])

  // Auto-save on graph edits (debounced). Depend on doc?.id (stable) not doc (mutates on save).
  const docId = doc?.id
  useEffect(() => {
    if (!docId || !didHydrateRef.current) return
    if (lastSyncedTextRef.current === null) return
    // No change vs. last synced state → nothing to save
    if (jsonText === lastSyncedTextRef.current) return

    const parsed = tryParse(jsonText)
    if (!parsed.ok) return

    const storageId = useGraphStore.getState().docId
    if (storageId !== docId) return

    // Write to draft localStorage immediately
    try {
      window.localStorage.setItem(DRAFT_KEY_PREFIX + docId, jsonText)
    } catch {
      /* ignore */
    }

    markDirty()

    if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current)
    const textAtStart = jsonText
    saveTimerRef.current = window.setTimeout(async () => {
      await save(parsed.value)
      lastSyncedTextRef.current = textAtStart
      try {
        window.localStorage.removeItem(DRAFT_KEY_PREFIX + docId)
      } catch {
        /* ignore */
      }
    }, AUTO_SAVE_DEBOUNCE_MS)

    return () => {
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current)
    }
  }, [jsonText, docId, save, markDirty])

  if (!id) {
    navigate('/graph')
    return null
  }

  if (loadError) {
    return (
      <div className="h-dvh w-full flex items-center justify-center bg-background">
        <div className="text-center">
          <p
            className="text-[14px] font-light text-destructive mb-4"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            {loadError}
          </p>
          <button
            onClick={() => navigate('/graph')}
            className="text-[12px] underline underline-offset-4 font-light"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            Back to documents
          </button>
        </div>
      </div>
    )
  }

  if (loading || !doc || !didHydrateRef.current) {
    return (
      <div className="h-dvh w-full flex items-center justify-center bg-background">
        <div className="w-5 h-5 border border-foreground/20 border-t-foreground rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <ReactFlowProvider>
      <div className="h-dvh w-full flex flex-col bg-background overflow-hidden">
        <DocHeader />
        <div className="flex-1 flex overflow-hidden min-h-0">
          <div className="flex-shrink-0 overflow-hidden" style={{ width: leftPaneWidth }}>
            <JsonPane />
          </div>
          <ResizableDivider onResize={(d) => setLeftPaneWidth(leftPaneWidth + d)} />
          <div className="flex-1 overflow-hidden">
            <CanvasPane />
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  )
}
